import {
  Box,
  Button,
  Paper,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  MenuItemData,
  ThreeDotsMenu,
} from '../../../components/documented/ThreeDotsMenu';
import NoData from '../../../components/NoData';
import PathCircularProgress from '../../../components/PathCircularProgress';
import SearchBar from '../../../components/SearchBar/SearchBar';
import { InvitationCode } from '../../../model/admin/inviteCode';
import { getActiveInvites } from '../../../redux/actions/adminActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectInvites } from '../../../redux/selectors/adminSelectors';
import { inviteService } from '../../../services/inviteService';
import {
  getComparator,
  Order,
  StyledTableCell,
  StyledTableRow,
} from '../TableComponents';

const TableDetailContainer = styled(Box)(() => ({
  boxSizing: 'border-box',
  height: 'fit-content',
  width: '100%',
  padding: '10px 40px 10px 20px',
}));

export interface HeadCell {
  id: keyof InvitationCode;
  label: string;
  numeric: boolean;
  toolTip?: string;
}

export interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof InvitationCode
  ) => void;
  order: Order;
  orderBy: string;
}

function HealthcareProfessionalAccountsTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const { t } = useTranslation(['admin', 'common']);
  const createSortHandler =
    (property: keyof InvitationCode) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'firstName',
      numeric: false,
      label: t('common:fields.firstName'),
    },
    {
      id: 'lastName',
      numeric: false,
      label: t('common:fields.lastName'),
    },
    {
      id: 'email',
      numeric: false,
      label: t('common:fields.email'),
    },
    {
      id: 'invitedBy',
      numeric: false,
      label: t('manageInvites.invitedBy'),
    },
    {
      id: 'created',
      numeric: false,
      label: t('manageInvites.created'),
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <Tooltip title={headCell.toolTip || ''}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? t('common:table.sortedDescending')
                      : t('common:table.sortedAscending')}
                  </Box>
                ) : null}
              </TableSortLabel>
            </Tooltip>
          </StyledTableCell>
        ))}
        <StyledTableCell align="left">
          {t('manageInvites.inviteEmail')}
        </StyledTableCell>
        <StyledTableCell align="left">
          {t('common:table.userActions')}
        </StyledTableCell>
      </TableRow>
    </TableHead>
  );
}

interface Props {
  isLoading: boolean;
}

export const ManageInvitesComponent: React.FC<Props> = ({ isLoading }) => {
  const { t } = useTranslation(['admin', 'common']);
  const invites = useSelector(selectInvites);
  const dispatch = useAppThunkDispatch();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] =
    React.useState<keyof InvitationCode>('lastName');
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const filteredInvites = React.useMemo(() => {
    try {
      // If no search term, return all invites
      if (!searchTerm) return invites;

      // Validate invites array
      if (!invites || !Array.isArray(invites)) {
        return [];
      }

      // Get lowercase search term
      const searchLower = String(searchTerm).toLowerCase();

      // Filter invites with error handling
      return invites.filter((invite) => {
        try {
          if (!invite) return false;

          const firstName = invite.firstName
            ? invite.firstName.toLowerCase()
            : '';
          const lastName = invite.lastName ? invite.lastName.toLowerCase() : '';
          const fullName = `${firstName} ${lastName}`;
          const email = invite.email ? invite.email.toLowerCase() : '';

          return (
            firstName.includes(searchLower) ||
            lastName.includes(searchLower) ||
            fullName.includes(searchLower) ||
            email.includes(searchLower)
          );
        } catch (err) {
          console.error('Error filtering invite:', err);
          return false;
        }
      });
    } catch (err) {
      console.error('Error in filteredInvites:', err);
      return [];
    }
  }, [invites, searchTerm]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof InvitationCode
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function handleResendClick(email: string) {
    try {
      inviteService.resendInviteRequest(email).then((response: any) => {});
    } catch (error: any) {
      //hmmm
    }
  }

  function handleDeleteInvite(inviteCodeGuid: string) {
    try {
      inviteService.deleteInvite(inviteCodeGuid).then((response: any) => {
        dispatch(getActiveInvites()).then((response: any) => {});
      });
    } catch (error: any) {
      //hmmm
    }
  }

  const menuItems: MenuItemData[] = [
    {
      label: t('common:actions.remove'),
      action: (data) => handleDeleteInvite(data),
    },
  ];

  return (
    <TableDetailContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Typography
          sx={{ padding: '5px 0px 5px 0px' }}
          variant="h5"
          component="h2"
        >
          {t('manageInvites.title')}
        </Typography>
        {!isLoading && invites.length > 0 && (
          <SearchBar
            placeholder={t('manageInvites.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        )}
      </Box>
      {!isLoading ? (
        invites.length > 0 && filteredInvites.length > 0 ? (
          <Paper sx={{ width: '100%', mb: 2 }}>
            <TableContainer sx={{ maxHeight: 490, minWidth: 750 }}>
              <Table stickyHeader aria-labelledby="pending-invites-table">
                <HealthcareProfessionalAccountsTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredInvites
                    .slice()
                    .sort(getComparator(order, orderBy))
                    .map((invite) => {
                      return (
                        <StyledTableRow
                          hover
                          tabIndex={-1}
                          key={invite.invitationCodeGuid}
                        >
                          <StyledTableCell align="left">
                            {invite.firstName}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {invite.lastName}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {invite.email}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {invite.invitedBy}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {new Date(invite.created).toDateString()}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Button
                              onClick={() => handleResendClick(invite.email)}
                            >
                              {t('common:actions.resend')}
                            </Button>
                          </StyledTableCell>
                          <StyledTableCell>
                            <ThreeDotsMenu
                              data={invite.invitationCodeGuid}
                              menuItems={menuItems}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : invites.length > 0 && filteredInvites.length === 0 ? (
          <NoData
            message={t('manageInvites.noneMatching', { term: searchTerm })}
          />
        ) : (
          <NoData message={t('manageInvites.empty')} />
        )
      ) : (
        <Box
          data-testid="loading-indicator" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100px', justifyContent: 'center' }}>
          <PathCircularProgress />
        </Box>
      )}
    </TableDetailContainer>
  );
};

export default ManageInvitesComponent;
